import React from 'react';
import { Heart, Mountain, Users, PartyPopper, Gem, Wallet } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Small themed pill that surfaces the AI-detected travel style
 * (budget / romantic / adventure / family / nightlife / luxury) on the
 * trip-summary card. Helps users instantly verify the AI understood the
 * vibe of their request — and gives the chat a more "concierge" feel.
 *
 * Renders nothing for unknown / standard styles to keep noise down.
 */
// V3.5 palette — every style tone maps to a 3-tone family so the badge
// stack on the TripSummaryCard never breaks brand purity, regardless of
// which style the AI infers.
const STYLE_CONFIG = {
  budget: {
    Icon: Wallet,
    classes: 'bg-emerald-500/15 text-emerald-200 ring-emerald-400/30',
    labelKey: 'chat.style.budget',
  },
  romantic: {
    Icon: Heart,
    classes: 'bg-brand-amber-400/15 text-brand-amber-200 ring-brand-amber-400/30',
    labelKey: 'chat.style.romantic',
  },
  adventure: {
    Icon: Mountain,
    classes: 'bg-brand-amber-500/15 text-brand-amber-200 ring-brand-amber-400/30',
    labelKey: 'chat.style.adventure',
  },
  family: {
    Icon: Users,
    classes: 'bg-blue-500/15 text-blue-200 ring-blue-400/30',
    labelKey: 'chat.style.family',
  },
  nightlife: {
    Icon: PartyPopper,
    classes: 'bg-blue-600/15 text-blue-100 ring-blue-500/30',
    labelKey: 'chat.style.nightlife',
  },
  luxury: {
    Icon: Gem,
    classes: 'bg-brand-navy-800/40 text-white ring-brand-navy-400/40',
    labelKey: 'chat.style.luxury',
  },
};

export default function TripStyleBadge({ style }) {
  const { t } = useLanguage();
  if (!style) return null;
  const cfg = STYLE_CONFIG[String(style).toLowerCase()];
  if (!cfg) return null;
  const { Icon, classes, labelKey } = cfg;
  return (
    <span
      data-testid={`chat-trip-style-${style}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-widest ring-1 ${classes}`}
    >
      <Icon className="w-3 h-3" />
      {t(labelKey)}
    </span>
  );
}
